import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { currentDateTime, wtFormatDate } from 'src/helpers/commons/moment'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney } from 'src/helpers/commons/utils'

const user = wtGetFromLocalStorage('authUser')

const DEFAULT_OPTIONS = {
  reportTitle: 'TRIAL BALANCE',
  detailTitle: 'TRIAL BALANCE DETAIL',
  asOf: '',
  branchName: '',
  companyName: 'WAH INDUSTRIES LIMITED',
}

const formatReportValue = (value) => {
  const numericValue = Math.abs(Number(value || 0))
  return wtFormatMoney(numericValue, '', true, 2).replace(/\s+/g, '')
}

const Pdf = async (trialBalanceData, options = {}) => {
  const settings = { ...DEFAULT_OPTIONS, ...options }

  const generatePdf = (reportData) => {
    if (!reportData || reportData.length === 0) {
      lzNotifyErrorMessageFromResult('No Data found!')
      return
    }

    const doc = new jsPDF('landscape')
    const totalPagesExp = '{total_pages_count_string}'
    const logo = 'assets/wil-icon.png'
    const flattenedData = flattenData(reportData)
    const summaryData = getFirstLevelData(reportData)

    const header = (title) => {
      const pageSize = doc.internal.pageSize
      const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()

      doc.setFontSize(11)
      doc.setTextColor('#3c4b64')
      doc.setFont('Helvetica', 'bold')
      try {
        doc.addImage(logo, 'PNG', 5, 4, 25, 25)
      } catch (error) {
        // Keep pdf generation functional if logo is unavailable.
      }
      doc.line(32, 6, 32, 25)

      doc.text('WAH', 35, 10, { align: 'left' })
      doc.text('INDUSTRIES', 35, 15)
      doc.text('LIMITED', 35, 20)
      doc.setFontSize(9)
      doc.text('www.wahindustries.com', 35, 25)
      doc.setFontSize(11)
      doc.text(title, pageWidth - 92, 15, { align: 'left' })

      const asOfText = settings.asOf ? wtFormatDate(settings.asOf, 'MMMM D, Y') : 'N/A'
      doc.text('AS OF ' + asOfText, pageWidth - 92, 22, { align: 'left' })
      doc.setFontSize(11)
      doc.setTextColor('black')
      doc.setFont('Helvetica', 'bold')
      doc.text('Branch: ', pageWidth - 92, 28, { align: 'left' })
      doc.setTextColor('#0b3d91')
      doc.text(settings.branchName || 'All', pageWidth - 74, 28, { align: 'left' })

      doc.line(10, 33, pageWidth - 10, 33)
    }

    const footer = () => {
      const pageNo = doc.internal.getNumberOfPages()
      const pageSize = doc.internal.pageSize
      const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
      const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()

      doc.setFontSize(10)
      doc.setTextColor('black')
      doc.setFont('Helvetica', 'normal')
      doc.text('This report is printed through ERP at ', 60, pageHeight - 15)

      doc.setTextColor('#0202f7')
      doc.setFont('Helvetica', 'bold')
      doc.text(currentDateTime(), 124, pageHeight - 15)

      let str = 'Page ' + pageNo
      if (typeof doc.putTotalPages === 'function') {
        str = str + ' of ' + totalPagesExp
      }

      doc.setTextColor('black')
      doc.setFontSize(9)
      doc.line(14, pageHeight - 12, pageWidth - 14, pageHeight - 12)
      doc.text(str, pageWidth - 12, pageHeight - 8, { align: 'right' })
      doc.text('Printed By ', 20, pageHeight - 8, { align: 'left' })
      doc.setTextColor('blue')
      doc.text(`${user ? user.name : ''}`, 37, pageHeight - 8, { align: 'left' })
    }

    const headRows = () => [
      [
        { content: '', styles: { halign: 'left', cellWidth: 76 } },
        {
          content: 'Opening Balance\n(Rupees)',
          colSpan: 2,
          styles: { halign: 'center', cellWidth: 44 },
        },
        {
          content: 'Current Period\n(Rupees)',
          colSpan: 2,
          styles: { halign: 'center', cellWidth: 44 },
        },
        {
          content: 'Closing Balance\n(Rupees)',
          colSpan: 2,
          styles: { halign: 'center', cellWidth: 44 },
        },
      ],
      [
        { content: '', styles: { halign: 'left', cellWidth: 76 } },
        { content: 'Debit', styles: { halign: 'center', cellWidth: 22 } },
        { content: 'Credit', styles: { halign: 'center', cellWidth: 22 } },
        { content: 'Debit', styles: { halign: 'center', cellWidth: 22 } },
        { content: 'Credit', styles: { halign: 'center', cellWidth: 22 } },
        { content: 'Debit', styles: { halign: 'center', cellWidth: 22 } },
        { content: 'Credit', styles: { halign: 'center', cellWidth: 22 } },
      ],
    ]

    const summaryRows = (rows) =>
      rows.map((row) => [
        { content: row.name, styles: { halign: 'left' } },
        { content: formatReportValue(row.opening_balance_debit), styles: { halign: 'right' } },
        { content: formatReportValue(row.opening_balance_credit), styles: { halign: 'right' } },
        { content: formatReportValue(row.current_period_debit), styles: { halign: 'right' } },
        { content: formatReportValue(row.current_period_credit), styles: { halign: 'right' } },
        { content: formatReportValue(row.closing_balance_debit), styles: { halign: 'right' } },
        { content: formatReportValue(row.closing_balance_credit), styles: { halign: 'right' } },
        { content: row.id === 'total', styles: { display: 'none' } },
      ])

    const detailRows = (rows) =>
      rows.map((row) => [
        {
          content: row.name,
          styles: {
            halign: 'left',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
            fontSize: row.rowToBold ? 10 : 9,
          },
        },
        {
          content: formatReportValue(row.opening_balance_debit),
          styles: {
            halign: 'right',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
          },
        },
        {
          content: formatReportValue(row.opening_balance_credit),
          styles: {
            halign: 'right',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
          },
        },
        {
          content: formatReportValue(row.current_period_debit),
          styles: {
            halign: 'right',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
          },
        },
        {
          content: formatReportValue(row.current_period_credit),
          styles: {
            halign: 'right',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
          },
        },
        {
          content: formatReportValue(row.closing_balance_debit),
          styles: {
            halign: 'right',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
          },
        },
        {
          content: formatReportValue(row.closing_balance_credit),
          styles: {
            halign: 'right',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
          },
        },
        { content: row.rowToUnderline, styles: { display: 'none' } },
        { content: row.id === 'total', styles: { display: 'none' } },
      ])

    const applySharedTableStyle = (tableTitle, body, underlineColumnIndex) => {
      doc.autoTable({
        head: headRows(),
        body,
        theme: 'plain',
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [60, 75, 100],
          fontSize: 10,
        },
        columnStyles: {
          0: { halign: 'left', cellWidth: 76, minCellHeight: 6, font: 'helvetica', fontSize: 8 },
          1: { halign: 'right', cellWidth: 22, minCellHeight: 6, font: 'helvetica', fontSize: 8 },
          2: { halign: 'right', cellWidth: 22, minCellHeight: 6, font: 'helvetica', fontSize: 8 },
          3: { halign: 'right', cellWidth: 22, minCellHeight: 6, font: 'helvetica', fontSize: 8 },
          4: { halign: 'right', cellWidth: 22, minCellHeight: 6, font: 'helvetica', fontSize: 8 },
          5: { halign: 'right', cellWidth: 22, minCellHeight: 6, font: 'helvetica', fontSize: 8 },
          6: { halign: 'right', cellWidth: 22, minCellHeight: 6, font: 'helvetica', fontSize: 8 },
        },
        styles: {
          overflow: 'linebreak',
          cellPadding: { top: 1.3, right: 1, bottom: 1.3, left: 1 },
        },
        margin: { top: 43, bottom: 20, left: 10 },
        didDrawPage: () => {
          header(tableTitle)
          footer()
        },
        willDrawCell: (data) => {
          if (data.row.section === 'head' && data.column.index > 0) {
            const { cell } = data
            doc.setDrawColor(0, 0, 0)
            doc.setLineWidth(1)
            doc.line(cell.x + 2, cell.y + cell.height, cell.x + cell.width - 2, cell.y + cell.height)
          }

          if (
            data.row.section === 'body' &&
            data.row.raw[underlineColumnIndex] &&
            data.row.raw[underlineColumnIndex].content === true &&
            data.column.index > 0
          ) {
            const { cell } = data
            doc.setDrawColor(0, 0, 0)
            doc.setLineWidth(0.5)
            doc.line(cell.x + 2, cell.y, cell.x + cell.width - 2, cell.y)
            doc.line(cell.x + 2, cell.y + 6, cell.x + cell.width - 2, cell.y + 6)
          }
        },
        didParseCell: (data) => {
          if (data.section === 'head' && (data.column.index === 1 || data.column.index === 2)) {
            data.cell.styles.textColor = [14, 116, 144]
          }
          if (data.section === 'head' && (data.column.index === 3 || data.column.index === 4)) {
            data.cell.styles.textColor = [166, 76, 29]
          }
          if (data.section === 'head' && (data.column.index === 5 || data.column.index === 6)) {
            data.cell.styles.textColor = [40, 122, 86]
          }
          if (data.section === 'body' && (data.column.index === 1 || data.column.index === 2)) {
            data.cell.styles.textColor = [14, 116, 144]
          }
          if (data.section === 'body' && (data.column.index === 3 || data.column.index === 4)) {
            data.cell.styles.textColor = [166, 76, 29]
          }
          if (data.section === 'body' && (data.column.index === 5 || data.column.index === 6)) {
            data.cell.styles.textColor = [40, 122, 86]
          }
          if (data.section === 'body' && data.row.raw[underlineColumnIndex] && data.row.raw[underlineColumnIndex].content === true) {
            data.cell.styles.fontSize = 9.5
            data.cell.styles.fontStyle = 'bold'
          }
          if (data.section === 'head') {
            data.cell.styles.fontStyle = 'bold'
          }
        },
      })
    }

    applySharedTableStyle(settings.reportTitle, summaryRows(summaryData), 7)
    doc.addPage()
    applySharedTableStyle(settings.detailTitle, detailRows(flattenedData), 7)

    if (typeof doc.putTotalPages === 'function') {
      doc.putTotalPages(totalPagesExp)
    }

    const pdfBlob = doc.output('blob')
    const pdfUrl = URL.createObjectURL(pdfBlob)
    window.open(pdfUrl, '_blank')
  }

  generatePdf(trialBalanceData)
}

const getFirstLevelData = (data = []) =>
  data.map((row) => ({
    id: row.id,
    name: row.name,
    opening_balance_debit: Number(row.opening_balance_debit || 0),
    opening_balance_credit: Number(row.opening_balance_credit || 0),
    current_period_debit: Number(row.current_period_debit || 0),
    current_period_credit: Number(row.current_period_credit || 0),
    closing_balance_debit: Number(row.closing_balance_debit || 0),
    closing_balance_credit: Number(row.closing_balance_credit || 0),
  }))

const flattenData = (rows = [], depth = 0) => {
  let result = []

  rows.forEach((row) => {
    const indent = '    '.repeat(depth)
    const isGroup = row.id !== 'total' && (row.hasItems || Number(row.is_transaction_level) === 0)
    const isTotal = row.id === 'total'

    result.push({
      id: row.id,
      name: indent + row.name,
      opening_balance_debit: Number(row.opening_balance_debit || 0),
      opening_balance_credit: Number(row.opening_balance_credit || 0),
      current_period_debit: Number(row.current_period_debit || 0),
      current_period_credit: Number(row.current_period_credit || 0),
      closing_balance_debit: Number(row.closing_balance_debit || 0),
      closing_balance_credit: Number(row.closing_balance_credit || 0),
      rowToBold: isGroup || isTotal,
      rowToUnderline: isTotal,
    })

    if (Array.isArray(row.children) && row.children.length > 0) {
      result = result.concat(flattenData(row.children, depth + 1))
    }
  })

  return result
}

export default Pdf
